import createAPIRequest from '../helpers/createAPIRequest'
import getRaceDetails from '../helpers/getRaceDetails'
import getPlayerId from '../helpers/getPlayerId'
import isEmpty from '../helpers/isEmpty'
import store from '../store/index'
import { axios } from '@/common/api.service.js'

async function findMemberCampaignByArena({ arenaId, arenaName, headers }) {
  let nextUrl = 'api/v1/campaigns/member/'

  while (nextUrl) {
    const response = await axios.get(nextUrl, {
      headers,
      mode: 'same-origin',
    })

    const payload = response?.data || {}
    const campaigns = Array.isArray(payload.results) ? payload.results : []
    const match = campaigns.find(
      (entry) =>
        String(entry.id) === String(arenaId) ||
        (!isEmpty(arenaName) && String(entry.name) === String(arenaName)),
    )

    if (match) {
      return match
    }

    nextUrl = payload?.next ?? null
  }

  return null
}

async function findFirstMemberCampaign({ headers }) {
  const response = await axios.get('api/v1/campaigns/member/?limit=1', {
    headers,
    mode: 'same-origin',
  })

  const payload = response?.data || {}
  const campaigns = Array.isArray(payload.results) ? payload.results : []
  return campaigns[0] || null
}

async function fetchCampaignMemberships({ slug, headers }) {
  if (isEmpty(slug)) {
    return []
  }

  const response = await axios.get(`api/v1/campaigns/${slug}/players/`, {
    headers,
    mode: 'same-origin',
  })

  return Array.isArray(response?.data?.results) ? response.data.results : []
}

function findMembershipByCandidateValues({ memberships, candidateValues, arenaId }) {
  if (!Array.isArray(memberships) || memberships.length === 0) {
    return null
  }

  const normalizedCandidateValues = (candidateValues || [])
    .filter((value) => !isEmpty(value))
    .map((value) => String(value).toLowerCase())

  if (normalizedCandidateValues.length === 0) {
    return null
  }

  return (
    memberships.find((entry) => {
      const entryArena = entry?.arena == null ? null : String(entry.arena)
      if (!isEmpty(arenaId) && entryArena !== String(arenaId)) {
        return false
      }

      const membershipValues = [
        entry?.player,
        entry?.player_email,
        entry?.player_name,
        entry?.user_name,
        entry?.first_name,
      ]
        .filter((value) => !isEmpty(value))
        .map((value) => String(value).toLowerCase())

      return normalizedCandidateValues.some((candidate) => membershipValues.includes(candidate))
    }) || null
  )
}

function findMembershipForUser({ memberships, authUserProfile, playerName, arenaId }) {
  const selectedPlayerMembership = findMembershipByCandidateValues({
    memberships,
    candidateValues: [playerName],
    arenaId,
  })

  if (selectedPlayerMembership) {
    return selectedPlayerMembership
  }

  return findMembershipByCandidateValues({
    memberships,
    candidateValues: [
      authUserProfile?.id,
      authUserProfile?.email,
      authUserProfile?.user_name,
      authUserProfile?.first_name,
    ],
    arenaId,
  })
}

async function resolvePlayerMembershipId({ payload, combatState, authUserProfile, thisStore }) {
  if (isEmpty(payload?.arena) || isEmpty(authUserProfile?.id)) {
    return null
  }

  let campaignPlayers = thisStore.getters['combat/getCampaignPlayers'] || []
  let membership = findMembershipForUser({
    memberships: campaignPlayers,
    authUserProfile,
    playerName: payload?.player_name,
    arenaId: payload?.arena,
  })

  if (membership?.id) {
    return membership.id
  }

  let selectedCampaign = null

  if (String(combatState.arena?.id) === String(payload.arena)) {
    selectedCampaign = combatState.arena
  }

  if (!selectedCampaign && Array.isArray(combatState.arenas)) {
    selectedCampaign =
      combatState.arenas.find((entry) => String(entry.id) === String(payload.arena)) ||
      combatState.arenas.find((entry) => String(entry.name) === String(payload.arena_name)) ||
      null
  }

  if (!selectedCampaign?.slug) {
    selectedCampaign = await findMemberCampaignByArena({
      arenaId: payload.arena,
      arenaName: payload.arena_name,
      headers: combatState.headers,
    })
  }

  if (!selectedCampaign?.slug) {
    return null
  }

  campaignPlayers = await fetchCampaignMemberships({
    slug: selectedCampaign.slug,
    headers: combatState.headers,
  })
  membership = findMembershipForUser({
    memberships: campaignPlayers,
    authUserProfile,
    playerName: payload?.player_name,
    arenaId: payload?.arena,
  })

  return membership?.id ?? null
}

function hasValidPlayerMembershipId(playerValue) {
  const numericPlayerId = Number(playerValue)

  return Number.isInteger(numericPlayerId) && numericPlayerId > 0
}
//----------------
// edit character
//----------------
//-------------------------------------------------------------------------------------
// Notes:
// - supports optional preview flag: when preview=true the API will validate/serialise
// and return what would be created/updated without persisting.
// - preview mode: we return the raw dispatch result to the caller.
// - non-preview: we perform the normal create/update &
//   optionally update the image before returning a wrapper.
//-------------------------------------------------------------------------------------
export default async function useEditCharacter(
  changed_profile,
  { preview = false, minimalResponse = false } = {},
) {
  console.log('--------------------')
  console.log('IN useEditCharacter (preview =', preview, ')')
  //--------------
  // access  store
  //--------------
  const this_store = store
  const this_store_state = store.state.combat

  try {
    const payload = {
      ...(changed_profile?.payload || {}),
    }

    //-------------------------------
    // prepare payload & API request
    //-------------------------------
    let slug = ''
    let parm_slug = ''

    if (!isEmpty(changed_profile.slug)) {
      parm_slug = changed_profile.slug
    }

    let endpoint = 'api/v1/characters/'
    //
    const request_parms = {
      endpoint: preview ? endpoint + '?preview=true' : endpoint,
      slug: parm_slug,
      partialInd: !isEmpty(parm_slug),
    }

    const request = createAPIRequest(request_parms)
    if (minimalResponse) {
      request.endpoint += `${request.endpoint.includes('?') ? '&' : '?'}response=minimal`
    }
    const shouldResolveArenaContext =
      isEmpty(parm_slug) ||
      ['arena', 'arena_name', 'player', 'player_name'].some((key) =>
        Object.prototype.hasOwnProperty.call(payload, key),
      )

    const characterHistoryFile = payload.character_history_file ?? null

    // remove raw image field (not sent to API)
    delete payload.image
    delete payload.character_history_preview_url
    delete payload.character_history_name
    delete payload.character_history_file

    if (shouldResolveArenaContext) {
      // ensure arena id present
      if (isEmpty(payload.arena)) {
        payload.arena = this_store_state.arena?.id ?? null

        if (isEmpty(payload.arena)) {
          const fallbackCampaign = await findFirstMemberCampaign({
            headers: this_store_state.headers,
          })

          if (fallbackCampaign?.id) {
            payload.arena = fallbackCampaign.id

            if (isEmpty(payload.arena_name)) {
              payload.arena_name = fallbackCampaign.name || ''
            }
          }
        }
      }

      const authUserProfile = this_store.getters['auth/getUserProfile'] || {}

      if (!hasValidPlayerMembershipId(payload.player)) {
        payload.player = null
      }

      // convert player name -> id when provided
      if (!isEmpty(payload.player_name) && isEmpty(payload.player)) {
        const campaignPlayers = this_store.getters['combat/getCampaignPlayers'] || []
        let player_id = null

        if (
          Array.isArray(this_store_state.arena?.players) &&
          this_store_state.arena.players.length > 0
        ) {
          try {
            player_id = getPlayerId(
              payload.player_name,
              this_store_state.arena.players,
              campaignPlayers,
            )
          } catch {
            player_id = null
          }
        }

        if (!player_id && Array.isArray(campaignPlayers) && campaignPlayers.length > 0) {
          player_id =
            findMembershipByCandidateValues({
              memberships: campaignPlayers,
              candidateValues: [payload.player_name],
              arenaId: payload.arena,
            })?.id ?? null
        }

        if (player_id) {
          payload.player = player_id
        }
      }

      if (isEmpty(payload.player)) {
        payload.player = await resolvePlayerMembershipId({
          payload,
          combatState: this_store_state,
          authUserProfile,
          thisStore: this_store,
        })
      }
    }

    // convert race name -> id when provided
    if (!isEmpty(payload.race_name)) {
      try {
        const allRaces = this_store.getters['races/getRaces'] || []
        const characterRaces = this_store.getters['races/getCharacterRaces'] || []
        const availableRaces = characterRaces.length > 0 ? characterRaces : allRaces
        const race = getRaceDetails(payload.race_name, availableRaces)
        if (race?.id) {
          payload.race = race.id
        } else {
          throw new Error('race lookup failed')
        }
      } catch {
        alert(window.bbUiErrorMsg('race not found for = ' + payload.race_name))
      }
    }

    // build API params (mirror preview flag to store/action)
    const api_parms_character = {
      api_request: request,
      payload,
      api_preview: preview,
    }

    //=================================================
    // if preview requested -> return dispatch result
    // (do not attempt image update, do not modify DB)
    //=================================================
    if (preview) {
      console.log('useEditCharacter: performing preview dispatch')
      const previewResp = await this_store.dispatch('characters/editCharacter', api_parms_character)
      // return the raw dispatch response so caller can inspect serializer/data
      return previewResp
    }

    //===============================================
    // normal flow: perform create/update then image
    //===============================================
    const editResp = await this_store.dispatch('characters/editCharacter', api_parms_character)

    // build response wrapper
    const response_parms = {
      character: editResp.data,
      image: null,
      character_history: null,
    }

    // resolve slug for possible image upload
    slug = isEmpty(api_parms_character.payload.slug)
      ? editResp.data.slug
      : api_parms_character.payload.slug

    // handle optional image update (only in non-preview)
    const image_file = api_parms_character.payload.image_file
    if (image_file) {
      const api_parms_image = {
        slug,
        image_file,
      }
      const imageResp = await this_store.dispatch(
        'characters/changeCharacterImage',
        api_parms_image,
      )
      response_parms.image = imageResp?.image ?? imageResp?.data?.image ?? imageResp ?? null
    } else {
      // no image to update
      console.log('useEditCharacter - no image file provided')
    }

    if (characterHistoryFile) {
      const api_parms_history = {
        slug,
        character_history_file: characterHistoryFile,
      }
      const historyResp = await this_store.dispatch(
        'characters/changeCharacterHistory',
        api_parms_history,
      )
      response_parms.character_history =
        historyResp?.character_history ??
        historyResp?.data?.character_history ??
        historyResp ??
        null
    }

    // return assembled result
    return response_parms
    //
  } catch (err) {
    console.error('useEditCharacter error:', err)
    console.error('useEditCharacter status:', err?.response?.status)
    console.error('useEditCharacter response data:', err?.response?.data)
    console.error('useEditCharacter payload:', payload)
    // rethrow so callers can handle errors
    throw err
  }
}
